import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { RoomEvent, DataPacket_Kind } from 'livekit-client';

/**
 * Phase 28 — LiveKit data-channel chat hook.
 *
 * In-memory only (MVP — late joiners don't see history). Messages flow
 * over `room.localParticipant.publishData()` with topic `chat`. Moderation
 * actions (delete/ban) flow over topic `mod` and are accepted only when
 * sent by the host.
 *
 * Wire format (JSON, < 2kB per packet):
 *   {
 *     t: 'msg',          // 'msg' | 'del'
 *     id: uuid,
 *     author: { id, username, avatar_url, is_creator },
 *     body: string (<=400 chars),
 *     ts: epoch_ms,
 *     type: 'normal' | 'highlighted' | 'tip'
 *   }
 *
 * Returns: { messages, send, deleteMessage, connected }.
 */

const MAX_BODY = 400;
const MAX_BUFFER = 200;

const decode = (payload) => {
  try {
    const dec = new TextDecoder();
    return JSON.parse(dec.decode(payload));
  } catch {
    return null;
  }
};

const encode = (obj) => {
  const enc = new TextEncoder();
  return enc.encode(JSON.stringify(obj));
};

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const useLiveKitChat = ({ room, currentUser, isCreator = false, hostUserId = null }) => {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const seenIds = useRef(new Set());

  // Push a message into the local buffer, dedupe by id
  const append = useCallback((msg) => {
    if (!msg || !msg.id) return;
    if (seenIds.current.has(msg.id)) return;
    seenIds.current.add(msg.id);
    setMessages((prev) => {
      const next = [...prev, msg];
      if (next.length > MAX_BUFFER) next.splice(0, next.length - MAX_BUFFER);
      return next;
    });
  }, []);

  // Subscribe to incoming data packets
  useEffect(() => {
    if (!room) return undefined;
    const onData = (payload, _participant, _kind, topic) => {
      const data = decode(payload);
      if (!data || !data.t) return;
      if (topic && topic !== 'chat' && topic !== 'mod') return;
      if (data.t === 'msg') {
        append({
          id: data.id,
          author: data.author || { id: 'unknown', username: 'unknown' },
          body: String(data.body || '').slice(0, MAX_BODY),
          ts: Number(data.ts) || Date.now(),
          type: data.type || 'normal',
        });
      } else if (data.t === 'del' && data.id) {
        setMessages((prev) => prev.filter((m) => m.id !== data.id));
      }
    };
    room.on(RoomEvent.DataReceived, onData);
    setConnected(room.state === 'connected');
    const onConnected = () => setConnected(true);
    const onDisconnected = () => setConnected(false);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Disconnected, onDisconnected);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [room, append]);

  // Send a chat message
  const send = useCallback(async (body, { type = 'normal' } = {}) => {
    const clean = String(body || '').trim().slice(0, MAX_BODY);
    if (!clean || !room || !currentUser) return null;
    const msg = {
      t: 'msg',
      id: uuid(),
      author: {
        id: currentUser.id,
        username: currentUser.username,
        avatar_url: currentUser.avatar_url || '',
        is_creator: !!isCreator,
      },
      body: clean,
      ts: Date.now(),
      type,
    };
    try {
      const lp = room.localParticipant;
      if (!lp) return null;
      await lp.publishData(encode(msg), { reliable: true, topic: 'chat' });
      // Echo locally so the sender sees their own message immediately.
      append({
        id: msg.id,
        author: msg.author,
        body: msg.body,
        ts: msg.ts,
        type: msg.type,
      });
      return msg;
    } catch {
      return null;
    }
  }, [room, currentUser, isCreator, append]);

  // Host-only: delete a message for everyone
  const deleteMessage = useCallback(async (msgId) => {
    if (!room || !msgId) return;
    const lp = room.localParticipant;
    if (!lp) return;
    try {
      await lp.publishData(encode({ t: 'del', id: msgId }), { reliable: true, topic: 'mod' });
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
    } catch {
      /* noop */
    }
  }, [room]);

  // Stable identity — consumers bubble this object to parents via
  // useEffect([chat]); a fresh object every render caused a
  // "Maximum update depth exceeded" re-render loop (fixed Jun 2026).
  return useMemo(
    () => ({ messages, send, deleteMessage, connected }),
    [messages, send, deleteMessage, connected],
  );
};

export default useLiveKitChat;
// keep linter happy if not used
export { DataPacket_Kind };
